import React, { useEffect, useState } from "react";
import { Card, Button, Table, Popconfirm, Space } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteOutlined,
  ColumnHeightOutlined,
  BarcodeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  getProductUnits,
  deleteProductUnit,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import DownloadImportTemplate from "../../../components/DownloadImportTemplate";
import ImportItems from "../../../components/ImportItems";
import AddUnit from "./AddUnit";
import UnitFormModal from "./UnitFormModal";
import { handleApiError } from "../../../utils/errorHandler";

const ProductUnits = () => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [productUnitsTableData, setProductUnitsTableData] = useState([]);
  const [editUnit, setEditUnit] = useState(null);
  const { t } = useTranslation();

  const reloadUnits = () => {
    setReloadTableData((prevState) => !prevState);
  };

  useEffect(() => {
    setIsDataLoading(true);
    getProductUnits()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setProductUnitsTableData(response?.data?.data?.product_units || []);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, t]);

  const deleteUnitFunc = (id) => {
    deleteProductUnit(id)
      .then((response) => {
        const data = response?.data || response?.response?.data;

        if (data?.success === true) {
          reloadUnits();
          NotificationWithIcon(
            "success",
            t("unit_deleted"),
            t("unit_deleted_successfully"),
          );
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/products",
      breadcrumbName: t("products"),
      icon: <BarcodeOutlined />,
    },
    {
      path: "units",
      breadcrumbName: t("units"),
      icon: <ColumnHeightOutlined />,
    },
  ];

  const detailsPartColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <p>{index + 1}</p>;
      },
    },
    {
      title: t("name_en"),
      render: (record) => {
        return <>{record.name}</>;
      },
    },
    {
      title: t("name_ar"),
      render: (record) => {
        return <>{record.name_ar}</>;
      },
    },
  ];

  const actionPartColumn = [
    {
      title: t("action"),
      width: "100px",
      align: "center",
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              onClick={() => setEditUnit(record)}
              title={t("unit_edit")}
            >
              <EditOutlined />
            </Button>
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => deleteUnitFunc(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("units")}
          ghost={false}
          extra={[
            <AddUnit
              key="open-add-unit-modal"
              iconOnly={false}
              reloadCallback={reloadUnits}
            />,
            <DownloadImportTemplate type="unit" key="import-template" />,
            <ImportItems
              type="unit"
              callbackLoading={reloadUnits}
              key="import-items"
            />,
          ]}
        />
      </Card>
      <Card>
        <Table
          columns={[...detailsPartColumns, ...actionPartColumn]}
          dataSource={productUnitsTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>

      <UnitFormModal
        open={Boolean(editUnit)}
        mode="edit"
        initialValues={editUnit}
        onClose={() => setEditUnit(null)}
        onSuccess={reloadUnits}
      />
    </>
  );
};

export default ProductUnits;
